/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {afterNextRender, Component, computed, ElementRef, HostListener, OnInit, signal, ViewChild} from '@angular/core';
import {CommonHelperService} from '../../service/common-helper.service';
import {MomentumProgram} from '../../model/momentumProgram.model';
import {ModelYear} from '../../model/modelYear.model';
import {KtsTimingTemplateRow} from '../../model/ktsTimingTemplateRow.model';
import * as XLSX from 'xlsx';
import {ColDef, ColGroupDef, ColumnVisibleEvent} from 'ag-grid-community';
import {ButtonCellRendererComponent} from '../cell-renderer/button-cell-renderer.component';
import {GanttChart} from '../../model/GanttChart';
import {GanttChartComponent} from '../gantt-chart/gantt-chart.component';
import {GanttSection} from '../../model/GanttSection';
import {GanttItem} from '../../model/GanttItem';
import {ProgramRun} from '../../model/programRun';
import {ganttTileColumnCount, ganttTileOrigins, loadGanttExportFont, svgWithExportFont} from './gantt-export-tiles';
import {EditableGridComponent} from '../editable-grid-component';
import {ProgramTiming} from '../../model/programTiming';

function readExportErrorText(body: Blob): Promise<string> {
    if (typeof body.text === 'function') {
        return body.text();
    }
    return new Response(body).text();
}

@Component({
    selector: 'app-kts',
    templateUrl: './kts.component.html',
    styleUrls: ['./kts.component.scss'],
    standalone: false
})

export class KtsComponent extends EditableGridComponent implements OnInit {
  @ViewChild('imageExportElement') imgExportElementRef: ElementRef;
  @ViewChild('exportGantt') exportGanttChart?: GanttChartComponent;
    types = ['PROD', 'AVD', 'ECS'];
    private _type = 'PROD';
    readonly typeSignal = signal<string>('PROD');

    get type(): string {
        return this._type;
    }

    set type(value: string) {
        this._type = value;
        this.typeSignal.set(value);
    }
    saving: boolean;
    fileNamePrefix = '';
    saveUrl = '/esciUI/saveKtsProgramTimingRuns';
    activeTabIndex = 0;
    readonly activeTabIndexSignal = signal(0);
    permissionUrl = '/esciUI/ktsPermission';
    templateUrl = '/esciUI/ktsTimingTemplates';
    modelYears = new Array<ModelYear>;
    selectedModelYear: ModelYear;
    selectedMomentumProgram: MomentumProgram;
    public static savedTimingPrefix = '(OLD) ';
    public static readonly MAX_LEGACY_V5_MINOR = 6;
    private readonly excelSheetNameMax = 31;
    // only used from admin page
    selectedVersion: string;
    protected readonly Array = Array;
    selectedMomentumProgramList = new Array<MomentumProgram>;
    // key is the raw program name used in programData/headerName; value is display classification suffix
    programClassificationMap = new Map<string, string>();
    // This list holds a large amount of data without details for user to pick from
    momentumProgramMap = new Map<String, Array<MomentumProgram>>;
    readonly ecsMomentumPrograms = signal<MomentumProgram[]>([]);
    readonly selectedEcsRpo = signal<string | null>(null);
    readonly selectedEcsModelYear = signal<string | null>(null);
    readonly selectedEcsProgram = signal<MomentumProgram | null>(null);
    readonly ecsRpoOptions = computed(() => {
        const rpos = new Set<string>();
        this.ecsRowsMatching(null, this.selectedEcsModelYear(), MomentumProgram.ecsProgramOptionKey(this.selectedEcsProgram()))
            .forEach(p => {
                if (p.gpsRpo) {
                    rpos.add(p.gpsRpo);
                }
            });
        return Array.from(rpos).sort((a, b) => a.localeCompare(b));
    });
    readonly ecsModelYearOptions = computed(() => {
        const years = new Set<string>();
        this.ecsRowsMatching(this.selectedEcsRpo(), null, MomentumProgram.ecsProgramOptionKey(this.selectedEcsProgram()))
            .forEach(p => {
                if (p.modelYear) {
                    years.add(p.modelYear);
                }
            });
        return Array.from(years).sort((a, b) => a.localeCompare(b));
    });
    readonly ecsProgramOptions = computed(() => {
        const programs = new Map<string, MomentumProgram>();
        this.ecsRowsMatching(this.selectedEcsRpo(), this.selectedEcsModelYear(), null).forEach(p => {
            const optionKey = MomentumProgram.ecsProgramOptionKey(p);
            if (optionKey && !programs.has(optionKey)) {
                programs.set(optionKey, p);
            }
        });
        return Array.from(programs.values()).sort((a, b) =>
            (a.projectName || '').localeCompare(b.projectName || ''));
    });
    canLoadProgramTiming(): boolean {
        if (this.type !== 'ECS') {
            return !!this.selectedMomentumProgram;
        }
        // GPS RPO is part of ECS identity. Model year is required so resolveEcsMergedProgram()
        // cannot merge milestone dates from different years that share the same persisted identity.
        if (!this.selectedEcsRpo() || !this.selectedEcsProgram() || !this.selectedEcsModelYear()) {
            return false;
        }
        const matches = this.ecsRowsMatching(
            this.selectedEcsRpo(),
            this.selectedEcsModelYear(),
            MomentumProgram.ecsProgramOptionKey(this.selectedEcsProgram()));
        const identities = new Set(matches
            .filter(p => !!p.projectId && !!p.vehProjectId)
            .map(p => MomentumProgram.getProgramKey(p, 'ECS')));
        return identities.size === 1;
    }
    // Key is type (PROD/AVD/ECS) which then goes to a map with version as key to template rows map
    ktsTimingTemplateRowMap = new Map<string, Map<string, Array<KtsTimingTemplateRow>>>();
    readonly ktsTimingTemplateRowMapSignal = signal<Map<string, Map<string, Array<KtsTimingTemplateRow>>>>(new Map());
    loadingMomentumData = false;
    readonly loadingMomentumDataSignal = signal(false);
    readonly selectedMomentumProgramCount = signal(0);
    readonly hasSelectedMomentumPrograms = computed(() => this.selectedMomentumProgramCount() > 0);
    readonly isTemplateTypeSelectorDisabled = computed(
        () => this.loadingMomentumDataSignal() || this.hasSelectedMomentumPrograms()
    );
    protected readonly KtsTimingTemplateRow = KtsTimingTemplateRow;
    // study mode only in admin page for now, but need scope here to put "_study" in report names
    studyMode = false;
    downloadButtonOptions = [
        {
            label: 'Export General',
            icon: 'pi pi-download',
            command: () => {
                this.exportTimingReport(false);
            }
        },
        {
            label: 'eSOR Timing Attachment',
            icon: 'pi pi-download',
            command: () => {
                this.exportTimingReport(true);
            }
        },
        {
            label: 'Export 30 Days',
            icon: 'pi pi-download',
            command: () => {
                this.exportTimingReport(false, 'within30Days');
            }
        },
        {
            label: 'Export 60 Days',
            icon: 'pi pi-download',
            command: () => {
                this.exportTimingReport(false, 'within60Days');
            }
        },
        {
            label: 'Export 90 Days',
            icon: 'pi pi-download',
            command: () => {
                this.exportTimingReport(false, 'within90Days');
            }
        },
    ];
    avdDownloadButtonOptions = this.downloadButtonOptions.filter(option => !option.label.includes('eSOR'));
    durationButtonOptions = [
        {
            label: 'Within 30 Days',
            icon: 'pi pi-filter',
            command: () => {
                this.filterByDuration(30);
            }
        },
        {
            label: 'Within 60 Days',
            icon: 'pi pi-filter',
            command: () => {
                this.filterByDuration(60);
            }
        },
        {
            label: 'Within 90 Days',
            icon: 'pi pi-filter',
            command: () => {
                this.filterByDuration(90);
            }
        },
        {
            label: 'Reset',
            icon: 'pi pi-filter-slash',
            command: () => {
                this.filterByDuration(null);
            }
        },
    ];
    public filterDuration: number;
    public paginationPageSize = 500;
    public paginationPageSizeSelector: number[] | boolean = [100, 200, 500];
    cols: (ColDef | ColGroupDef)[] = [
        {field: 'role', headerName: 'Role'},
        {field: 'phase', headerName: 'Phase', maxWidth: 200},
        {
            headerName: 'Deliverable & Description',
            marryChildren: true,
            children: [
                {field: 'deliverable', headerName: 'Deliverable', minWidth: 300, maxWidth: 1200},
                {field: 'description', headerName: 'Description', columnGroupShow: 'open', minWidth: 100, maxWidth: 1200},
            ],
        },
        {field: 'milestone', headerName: 'Milestone', minWidth: 100, maxWidth: 300},
        {
            field: 'weeksFromMilestone', headerName: 'Weeks From Milestone', minWidth: 100, maxWidth: 200,
            cellStyle: {'text-align': 'center'}, floatingFilter: false
        },
        {field: 'within30Days', headerName: 'Within 30 Days', hide: true},
        {field: 'within60Days', headerName: 'Within 60 Days', hide: true},
        {field: 'within90Days', headerName: 'Within 90 Days', hide: true},
    ];
    fullCols: Map<string, Map<string, ColDef[]>> = new Map;
    defaultColDef: ColDef = {
        floatingFilter: true,
        resizable: true,
        sortable: true,
        filter: true,
        minWidth: 50,
        maxWidth: 100
    };
    ganttChart : GanttChart;
    // this boolean turns on the div that gets exported. but it slows the page down when always on
    exportingGantt: boolean;
    readonly exportingReport = signal(false);
    // for timing calculations
    milestoneMap: Map<string, string>;
    allowDuplicates = false;
    private icdeStageDeliverables = new Set<string>();
    private icdeStageDeliverablesByNormalized = new Map<string, string>();
    private icdePreviousTargetByProgramStage = new Map<string, Date>();
    private icdePreviousAdjustedWeeksByProgramStage = new Map<string, number>();
    // Tracks per-program ICDe auto-seeding status (used for overwrite messaging).
    private autoAdjustedPrograms = new Set<string>();
    private pendingEcsOverwrite = false;
    pendingEcsSelectedProgram: MomentumProgram | null = null;
    programRunsLookupFailed = false;
    get safeActiveTabIndex(): number {
        const tabCount = this.ktsTimingTemplateRowMap.get(this.type)?.size ?? 0;
        if (tabCount <= 0) {
            return 0;
        }
        const currentIndex = typeof this.activeTabIndexSignal() === 'number' ? this.activeTabIndexSignal() : 0;
        return Math.min(Math.max(0, currentIndex), tabCount);
    }

    ngOnInit(): void {
        // The custom button cell renderers in the ag-grid call the esci-service to notify us of a change to recalculate
        this.esciService.buttonClick$.subscribe((row) => {
            this.doTimingCalculations();
        });
        this.permission.set(false);
        this.esciService.getPermissionAndFileSizeLimit(this, false);
        this.esciService.getKtsTimingTemplates(this);
        this.loadProgramsForType();
    }

    loadProgramsForType(): void {
        if (this.type === 'ECS') {
            this.esciService.getKtsEcsMomentumPrograms(this);
        } else {
            this.esciService.getMomentumProgramList(this);
        }
    }

    onTemplateTypeChange(): void {
        this.milestoneMap = undefined;
        this.resetVersionOverrideAndStudyMode();
        this.gridApiMap.clear();
        this.version = undefined;
        this.setActiveTabIndex(0);
        this.setLoadingMomentumData(false);
        this.resetEcsSelection();
        this.loadProgramsForType();
        this.filterByDuration(null);
        this.handleTabClick();
    }

    resetEcsSelection(): void {
        this.selectedEcsRpo.set(null);
        this.selectedEcsModelYear.set(null);
        this.selectedEcsProgram.set(null);
        this.selectedModelYear = null;
        this.selectedMomentumProgram = null;
    }

    onEcsRpoChange(rpo: string | null): void {
        this.selectedEcsRpo.set(rpo);
        if (this.selectedEcsModelYear() && this.ecsRowsMatching(rpo, this.selectedEcsModelYear(), null).length === 0) {
            this.selectedEcsModelYear.set(null);
        }
        if (this.selectedEcsProgram() && this.ecsRowsMatching(rpo, this.selectedEcsModelYear(),
            MomentumProgram.ecsProgramOptionKey(this.selectedEcsProgram())).length === 0) {
            this.selectedEcsProgram.set(null);
        }
        this.autoFillUniqueEcsSelections();
    }

    onEcsModelYearChange(modelYear: string | null): void {
        this.selectedEcsModelYear.set(modelYear);
        if (this.selectedEcsRpo() && this.ecsRowsMatching(this.selectedEcsRpo(), modelYear, null).length === 0) {
            this.selectedEcsRpo.set(null);
        }
        if (this.selectedEcsProgram() && this.ecsRowsMatching(this.selectedEcsRpo(), modelYear,
            MomentumProgram.ecsProgramOptionKey(this.selectedEcsProgram())).length === 0) {
            this.selectedEcsProgram.set(null);
        }
        this.autoFillUniqueEcsSelections();
    }

    onEcsProgramChange(program: MomentumProgram | null): void {
        this.selectedEcsProgram.set(program);
        const programKey = MomentumProgram.ecsProgramOptionKey(program);
        if (this.selectedEcsRpo() && this.ecsRowsMatching(this.selectedEcsRpo(), null, programKey).length === 0) {
            this.selectedEcsRpo.set(null);
        }
        if (this.selectedEcsModelYear() && this.ecsRowsMatching(null, this.selectedEcsModelYear(), programKey).length === 0) {
            this.selectedEcsModelYear.set(null);
        }
        this.autoFillUniqueEcsSelections();
    }

    private ecsRowsMatching(rpo?: string | null, modelYear?: string | null, programKey?: string | null): MomentumProgram[] {
        return this.ecsMomentumPrograms().filter(p =>
            (!rpo || p.gpsRpo === rpo) &&
            (!modelYear || p.modelYear === modelYear) &&
            (!programKey || MomentumProgram.ecsProgramOptionKey(p) === programKey)
        );
    }

    private autoFillUniqueEcsSelections(): void {
        for (let i = 0; i < 3; i++) {
            let changed = false;
            const rpos = this.ecsRpoOptions();
            if (!this.selectedEcsRpo() && rpos.length === 1) {
                this.selectedEcsRpo.set(rpos[0]);
                changed = true;
            }
            const years = this.ecsModelYearOptions();
            if (!this.selectedEcsModelYear() && years.length === 1) {
                this.selectedEcsModelYear.set(years[0]);
                changed = true;
            }
            const programs = this.ecsProgramOptions();
            const selectedProgram = this.selectedEcsProgram();
            if (!selectedProgram && programs.length === 1) {
                this.selectedEcsProgram.set(programs[0]);
                changed = true;
            } else if (selectedProgram) {
                const matchingProgram = programs.find(p =>
                    MomentumProgram.ecsProgramOptionKey(p) === MomentumProgram.ecsProgramOptionKey(selectedProgram));
                if (matchingProgram && (matchingProgram.gpsRpo !== selectedProgram.gpsRpo
                    || matchingProgram.modelYear !== selectedProgram.modelYear
                    || matchingProgram.projectId !== selectedProgram.projectId
                    || matchingProgram.vehProjectId !== selectedProgram.vehProjectId)) {
                    this.selectedEcsProgram.set(matchingProgram);
                    changed = true;
                }
            }
            if (!changed) {
                return;
            }
        }
    }

    getProgramKey(program?: MomentumProgram): string {
        const selected = program ?? this.selectedMomentumProgram;
        if (!selected) {
            return '';
        }
        return MomentumProgram.getProgramKey(selected, this.type);
    }

    findSelectedProgramByKey(programKey: string): MomentumProgram | undefined {
        return this.selectedMomentumProgramList.find(
            p => MomentumProgram.getProgramKey(p, this.type) === programKey
        );
    }

    getGridProgramKeyFromProgramRun(programRun: ProgramRun): string {
        if (programRun.type === 'ECS') {
            return `${programRun.gpsProjectId ?? ''}|${programRun.momentumId ?? ''}|${programRun.gpsRpo ?? ''}`;
        }
        return programRun.programName;
    }

    public setLoadingMomentumData(value: boolean): void {
        this.loadingMomentumData = value;
        this.loadingMomentumDataSignal.set(value);
    }

    public syncSelectedMomentumProgramState(): void {
        this.selectedMomentumProgramCount.set(this.selectedMomentumProgramList?.length ?? 0);
    }

    public syncKtsTimingTemplateRowMapState(): void {
        this.ktsTimingTemplateRowMapSignal.set(new Map(this.ktsTimingTemplateRowMap));
    }

    public setActiveTabIndex(value: number): void {
        const nextValue = Number.isFinite(value) ? Math.trunc(value) : 0;
        this.activeTabIndex = nextValue;
        this.activeTabIndexSignal.set(nextValue);
    }

    getMomentumProgramByMomentumId() {
        if (this.type === 'ECS') {
            this.addEcsProgramTiming();
            return;
        }
        this.setLoadingMomentumData(true);
        this.esciService.getMomentumProgramByMomentumId(this);
        // include archived runs so an existing (auto-archived) timing can still be found for compare/overwrite
        this.esciService.getProgramRuns(this, this.selectedMomentumProgram.programName, false, true,
            this.confirmWithUserToOverwrite);
    }

    addEcsProgramTiming() {
        const program = this.resolveEcsMergedProgram();
        if (!program) {
            this.messageService.processError('Select GPS RPO, model year and program before loading timing.');
            return;
        }
        const programKey = MomentumProgram.getProgramKey(program, this.type);
        if (!this.allowDuplicates && this.selectedMomentumProgramList.some(
            p => MomentumProgram.getProgramKey(p, this.type) === programKey)) {
            this.messageService.processError('You have already selected this program. ' +
                'Please choose a different unique program.');
            return;
        }
        this.setLoadingMomentumData(true);
        this.selectedMomentumProgram = program;
        this.selectedMomentumProgramList.push(program);
        this.syncSelectedMomentumProgramState();
        this.pendingEcsSelectedProgram = program;
        this.calculateKtsTimingWithProgram();
        if (!this.selectedMomentumProgramList.includes(program)) {
            this.pendingEcsSelectedProgram = null;
            this.setLoadingMomentumData(false);
            return;
        }
        const vehicleId = program.vehProjectId;
        this.esciService.getProgramRuns(this, vehicleId ? null : program.projectName, false, true,
            this.confirmWithUserToOverwrite, vehicleId ? [vehicleId] : undefined);
    }

    resolveEcsMergedProgram(): MomentumProgram | null {
        const base = this.selectedEcsProgram();
        if (!base) {
            return null;
        }
        const merged = structuredClone(base);
        const identity = MomentumProgram.getProgramKey(base, 'ECS');
        const siblings = this.ecsRowsMatching(
            this.selectedEcsRpo(),
            this.selectedEcsModelYear(),
            MomentumProgram.ecsProgramOptionKey(base)
        ).filter(row => MomentumProgram.getProgramKey(row, 'ECS') === identity);
        for (const sibling of siblings) {
            for (const key of Object.keys(sibling)) {
                if (!key.startsWith('date') || sibling[key] == null || sibling[key] === '') {
                    continue;
                }
                if (merged[key] == null || merged[key] === '') {
                    merged[key] = sibling[key];
                }
            }
        }
        return merged;
    }

    findSoleActiveEcsTemplateMajor(): string | null {
        const versionMap = this.ktsTimingTemplateRowMap.get(this.type);
        if (!versionMap) {
            return null;
        }
        const majors = new Set(Array.from(versionMap.keys())
            .filter(version => this.getStatusOfVersion(version, this.type) === 'ACTIVE')
            .map(version => KtsTimingTemplateRow.getMajorVersionFromFullVersion(version))
            .filter(major => !!major));
        return majors.size === 1 ? Array.from(majors)[0] : null;
    }

    confirmWithUserToOverwrite(component: KtsComponent, programName: string, programRun: ProgramRun) {
        {
            component.pendingEcsOverwrite = true;
            // pop up a confirmation so user knows this program run already exists and they have the option to compare
            component.confirmationService.confirm({
                message: `An existing Program Timing has been found for ${programName}. Do you want to run a <b>compare</b> against the previous version?`,
                header: 'Existing Program Timing Found',
                key: 'saveTimingsConfirmation',
                icon: 'pi pi-exclamation-circle',
                acceptButtonStyleClass: 'p-button-text',
                rejectButtonStyleClass: 'p-button-text',
                acceptIcon: 'pi pi-check mr-2',
                rejectIcon: 'pi pi-times mr-2',
                accept: () => {
                    // append 'OLD' to each program run name so user knows, and so we know not to save it
                    const comparisonKey = component.type === 'ECS'
                        ? KtsComponent.savedTimingPrefix + component.getGridProgramKeyFromProgramRun(programRun)
                        : KtsComponent.savedTimingPrefix + programRun.programName;
                    programRun.programName = comparisonKey;
                    component.showSavedProgramTiming(programRun);
                    component.pendingEcsOverwrite = false;
                },
                reject: () => {
                    const liveProgramKey = component.type === 'ECS'
                        ? component.getGridProgramKeyFromProgramRun(programRun)
                        : programRun.programName;
                    component.replaceIcdeTargetsFromProgramRun(programRun, liveProgramKey);
                    component.seedIcdeAdjustedWeeksForProgram(liveProgramKey);
                    component.doTimingCalculations();
                    component.pendingEcsOverwrite = false;
                    component.refreshGrid();
                    component.setupGantt();
                }
            });
        }
    }

    calculateKtsTimingWithProgram() {
        const versionMap = this.ktsTimingTemplateRowMap.get(this.type);
        const versionOverride = this.applySelectedVersionOverride();
        this.applyEcsVersionWhenNoOverride(versionOverride);
        if (this.resolveUnsupportedTemplateVersion(versionMap, versionOverride)) {
            return;
        }
        this.applyFastEvPptbVersion(versionMap);
        this.applyActiveFullVersionWhenUnlocked(versionOverride);
        this.version = this.selectedMomentumProgram.gvdpVersion;
        if (this.rejectAvdPre58Version()) {
            return;
        }
        if (this.rejectMissingTemplateRows(versionMap)) {
            return;
        }
        const programKey = this.getProgramKey(this.selectedMomentumProgram);
        if (this.rejectDuplicateProgram(versionMap, programKey)) {
            return;
        }
        this.doTimingCalculations();
        this.appendProgramColumn(programKey);
        this.refreshGrid();
        this.handleTabClick(this.version);
        this.selectedMomentumProgram = null;
        this.setupGantt();
    }

    private removeSelectedProgram() {
        const index = this.selectedMomentumProgramList.indexOf(this.selectedMomentumProgram);
        if (index >= 0) {
            this.selectedMomentumProgramList.splice(index, 1);
        }
        this.selectedMomentumProgram = null;
        this.syncSelectedMomentumProgramState();
    }

// This function generates a new column for the given program
    getProgramDateColumn(projectName: string, classification?: string, programTimingColumn?: boolean) {
        const col = {
            minWidth: 180,
            maxWidth: programTimingColumn ? 240 : 220,
            headerName: projectName,
            colId: projectName,
            headerValueGetter: () => this.formatProgramDisplayName(projectName, classification),
            wrapHeaderText: true,
            cellRenderer: ButtonCellRendererComponent,
            cellRendererParams: {},
            valueGetter: (params: any) => {
                // return the program specific date by using header as the key to the programData map
                return params.data.programData.get(params.colDef.headerName)?.date;
            },
            cellClassRules: {
                'program-column': () => !programTimingColumn,
                'program-timing-column': () => programTimingColumn,
                'within-30-days': params => params.data.programData.get(params.colDef.headerName)?.within30Days,
                'within-60-days': params => params.data.programData.get(params.colDef.headerName)?.within60Days,
                'within-90-days': params => params.data.programData.get(params.colDef.headerName)?.within90Days,
                'not-within-90-days': params => !params.data.programData.get(params.colDef.headerName)?.within90Days,
                'red-cell': params => params.data.programData.get(params.colDef.headerName)?.icdsNotOnSchedule > 0
            },
            headerClass: () => programTimingColumn ? 'program-timing-column-header' : 'program-column-header',
            filter: 'agDateColumnFilter',
            headerTooltip: 'Drag away from grid to remove',
            tooltipValueGetter: (params) => {
                const timing = params.data.programData.get(params.colDef.headerName);
                return timing?.icdsNotOnSchedule > 0 ? `${timing.icdsNotOnSchedule} ICDs not at Stage` : '';
            }
        };
        // Only the live (non-comparison) program columns are editable. Let a user enter a date on a blank
        // ICDe Stage row - base (incl. Stage 0) or Exterior Lighting - via the native ag-grid date editor
        // (same as the GPS Timing grid).
        if (!programTimingColumn) {
            col['editable'] = (params: any) => {
                const timing = params?.data?.programData?.get(params.colDef.headerName);
                return params?.data?.isIcdeStageRow()
                    && !params.data.studyMode
                    && (!timing?.date || !!timing?.manualDateEntered);
            };
            col['cellEditor'] = 'agDateCellEditor';
            col['cellDataType'] = 'date';
            col['valueSetter'] = (params: any) => this.setManualIcdeDate(params);
        }
        return col;
    }

    // Routes a date picked in the native ag-grid editor into the Map-backed programData (the column has no
    // `field`) and flags it as manual so recalculation preserves it. The downstream recompute + repaint of
    // dependent rows (e.g. the Exterior Lighting cascade) happens in onCellEditingStopped, which fires after
    // ag-grid finishes committing the edit.
    setManualIcdeDate(params: any): boolean {
        const programName = params.colDef.headerName;
        let programTiming = params.data.programData.get(programName);
        if (!programTiming) {
            programTiming = new ProgramTiming();
            programTiming.projectName = programName;
            params.data.programData.set(programName, programTiming);
        }
        let date: Date | undefined;
        if (params.newValue) {
            // Normalize to local midnight so it matches calculated dates and round-trips through the save/reload offsets.
            date = new Date(params.newValue);
            date.setHours(0, 0, 0, 0);
        }
        programTiming.date = date;
        programTiming.manualDateEntered = !!date;
        return true;
    }

    // Fires after a manual ICDe date edit is committed. Recompute so dependent rows (e.g. Exterior Lighting)
    // pick up the new base date, then force a cell refresh - the date column uses a valueGetter and the cells
    // render via a (non-Angular-event-driven) cellRenderer, so sibling cells will not repaint on their own.
    onCellEditingStopped(event: any): void {
        this.doTimingCalculations();
        if (event?.api && !event.api.isDestroyed()) {
            event.api.refreshCells({force: true});
        }
        this.setupGantt();
    }

    formatProgramDisplayName(programKey: string, classification?: string): string {
        const program = this.findSelectedProgramByKey(programKey);
        let displayName = program?.projectName ?? programKey;
        if (this.type === 'ECS' && program) {
            displayName = program.projectName || programKey;
            if (program.gpsRpo) {
                displayName = `${displayName} (${program.gpsRpo})`;
            }
        }
        const value = classification?.trim();
        if (!value) {
            return displayName;
        }
        return `${displayName} (${value})`;
    }

    public doTimingCalculations() {
        // first loop to populate the milestones if map is empty
        // need all milestones from all versions
        if (!this.milestoneMap) {
            this.milestoneMap = new Map();
            // iterate through a flat list of all values in the map from all versions
            for (const value of Array.prototype.concat.apply([], Array.from(this.ktsTimingTemplateRowMap.get(this.type).values()))) {
                // populate the milestone map by finding every row that has "milestone" phase
                // and putting it into a map where the key is the milestone Acronym
                if (value.phase === 'Milestone') {
                    // Set every milestone in the map like 'PFI' -> '10853'
                    // get the last five characters of deliverable which should be a 5-digit number
                    const match: RegExpExecArray | null = MomentumProgram.milestonePattern.exec(value.deliverable);
                    if (!match) {
                        console.warn(`Milestone ${value.deliverable} did not have a momentum date number`);
                    } else {
                        if (this.milestoneMap.get(value.milestone) && this.milestoneMap.get(value.milestone) !== match[0]) {
                            console.warn(`Milestone ${value.milestone} already existed as ${this.milestoneMap.get(value.milestone)}`);
                        }
                        this.milestoneMap.set(value.milestone, match[0]);
                    }
                }
            }
        }
        // now we can do the actual timing loops
        for (const value of this.ktsTimingTemplateRowMap.get(this.type).values()) {
            for (let i = 0; i < value.length; i++) {
                value[i].calculateDate(this.selectedMomentumProgramList, this.milestoneMap);
            }
            // Loop several more times to fill in dates that relied on non-milestone dates
            // third loop because with study mode, our first loop is not able to calculate anything but milestones with adjustments
            // this pushes us back a loop for reference dates that point to other adjusted dates that point to adjusted milestones
            // more loops because there are so many layers now with rows pointing to other rows
            // dave now has rows point to deliverables that are / separated instead of just milestones
            for (let j = 0; j < 5; j++) {
                for (let i = value.length - 1; i >= 0; i--) {
                    value[i].calculateReferenceDate(this.selectedMomentumProgramList, value);
                }
            }
        }
        // now set the three booleans for within 30/60/90
        this.setDurationBooleans();
    }
    setDurationBooleans() {
        const today = new Date();
        const thirtyDaysFromTodayDate = new Date();
        thirtyDaysFromTodayDate.setDate(today.getDate() + 30);
        const sixtyDaysFromTodayDate = new Date();
        sixtyDaysFromTodayDate.setDate(today.getDate() + 60);
        const ninetyDaysFromTodayDate = new Date();
        ninetyDaysFromTodayDate.setDate(today.getDate() + 90);
        for (const value of this.ktsTimingTemplateRowMap.get(this.type).values()) {
            // set the three booleans for within 30/60/90
            for (const item of value) {
                item.setDurationBooleans(today, thirtyDaysFromTodayDate, sixtyDaysFromTodayDate, ninetyDaysFromTodayDate);
            }
        }
    }

    exportTimingReport(forEsor?: boolean, durationFilter?: string) {
        // Snapshot before the Gantt render. That render can clear the live map, and reading it
        // afterwards produced an empty workbook ("Workbook is empty").
        const openVersions = new Map(this.ktsTimingTemplateRowMap.get(this.type) ?? []);
        this.exportingGantt = !forEsor;
        this.exportingReport.set(true);
        // After the next render so exportingGantt applies and the Gantt SVG has been painted.
        afterNextRender(() => {
            const wb = XLSX.utils.book_new();
            const timestamp = `_${CommonHelperService.getTimeStampForReport()}`;
            const study = this.studyMode ? '_Study' : '';
            const dur = durationFilter ? `_${durationFilter}` : '';
            const baseName = forEsor ? 'eSOR Timing Attachment' : (this.getPrefix() + ' Timing' + dur);
            const fileName = this.fileNamePrefix + baseName + study + timestamp + '.xlsx';
            const usedSheetNames = new Set<string>();

            for (const [key, value] of openVersions) {
                // Program dates can be absent on the first template row. Any later row still counts.
                const programData = this.mergedProgramData(value);
                if (!programData.size) {
                    continue;
                }
                const programNames = this.programNamesInGridOrder(key, programData);
                // Start with default template headers
                const headers = [
                    'Responsibility',
                    'Phase',
                    'DELIVERABLES/MILESTONES',
                    'Deliverable Description',
                    'milestone',
                    'weeks from milestone \n' +
                    `(GVDP ${key})`
                ];
                // add a new header to headers for each program name
                for (const programName of programNames) {
                    headers.push(this.formatProgramDisplayName(programName, this.programClassificationMap.get(programName)));
                }

                // if row is not included in ESOR report and forEsor is true, exclude it from the report
                let filteredData = value.filter(obj =>
                    (!forEsor || obj.includeInEsorReport === 'YES'));

                // filter data to only include rows that have a date within the duration filter
                if (durationFilter) {
                    filteredData = filteredData.filter(obj => obj[durationFilter]);
                }
                // Transform method to print regular timing values and then access program data by key
                const transformObjectToValues = (obj) => {
                    const newVar = [
                        obj.role,
                        obj.phase,
                        obj.deliverable,
                        obj.description,
                        obj.milestone,
                        obj.weeksFromMilestone
                    ];
                    // append to newVar for each date in programData in obj
                    for (const programName of programNames) {
                        // Print each programs date unless we have a duration filter, then only if date is within filter
                        // which is true if the [durationFilter] boolean is true
                        const timing = obj.programData?.get(programName);
                        if (!durationFilter || timing?.[durationFilter]) {
                            newVar.push(timing?.date ?? '');
                        } else {
                            newVar.push('');
                        }
                    }
                    return newVar;
                };
                const headersAndData = [headers, ...filteredData.map(transformObjectToValues)];
                const ws = XLSX.utils.aoa_to_sheet(headersAndData);
                XLSX.utils.book_append_sheet(wb, ws, this.timingSheetName(key, usedSheetNames));
            }
            this.exportProcess(forEsor, wb, fileName);
        }, {injector: this.injector});
    }

    private programNamesInGridOrder(version: string, programData: Map<string, unknown>): string[] {
        const liveOrder = this.gridApiMap?.get(version)?.getColumnState?.()
            ?.map(col => col.colId)
            .filter((id): id is string => !!id) ?? [];
        const definedOrder = (this.fullCols.get(this.type)?.get(version) ?? [])
            .map(col => col.colId)
            .filter((id): id is string => !!id);
        const onScreen = (liveOrder.length ? liveOrder : definedOrder)
            .filter(id => programData.has(id));
        const ordered = [...onScreen];
        for (const name of programData.keys()) {
            if (!ordered.includes(name)) {
                ordered.push(name);
            }
        }
        return ordered;
    }

    private mergedProgramData(rows: Array<{programData?: Map<string, unknown>}>): Map<string, unknown> {
        const merged = new Map<string, unknown>();
        for (const row of rows ?? []) {
            row?.programData?.forEach((timing, programName) => {
                if (!merged.has(programName)) {
                    merged.set(programName, timing);
                }
            });
        }
        return merged;
    }

    private timingSheetName(version: string, used: Set<string>): string {
        const cleaned = this.stripEdgeQuotes(`${this.getPrefix()} Timing ${version ?? ''}`
            .replace(/[\\/?*[\]:]/g, ' ')
            .trim());
        const base = (cleaned || 'Timing').slice(0, this.excelSheetNameMax);
        let candidate = base;
        let suffix = 2;
        while (used.has(candidate)) {
            const tail = ` ${suffix}`;
            candidate = `${base.slice(0, this.excelSheetNameMax - tail.length)}${tail}`;
            suffix++;
        }
        used.add(candidate);
        return candidate;
    }

    private stripEdgeQuotes(value: string): string {
        let start = 0;
        let end = value.length;
        while (start < end && value.charAt(start) === "'") {
            start++;
        }
        while (end > start && value.charAt(end - 1) === "'") {
            end--;
        }
        return start === 0 && end === value.length ? value : value.slice(start, end);
    }

    async exportProcess(forEsor, wb, fileName) {
        let ganttTiles = null;
        if (!forEsor) {
            try {
                ganttTiles = await this.captureGanttTiles(this.ganttExportSvg());
            } catch (error) {
                console.warn('Error capturing the image:', error);
            }
        }
        try {
            this.exportingReport.set(true);
            if (!wb?.SheetNames?.length) {
                this.messageService.processError('No program timing rows are open to export.');
                return;
            }
            const wbArrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = await this.downloadService.finishExportFile(wbArrayBuffer, ganttTiles).toPromise();
            CommonHelperService.downloadFile(blob, fileName);
        } catch (error) {
            await this.reportExportError(error);
        } finally {
            this.exportingGantt = false;
            this.exportingReport.set(false);
        }
    }

    private async reportExportError(error: unknown): Promise<void> {
        const fallback = 'Error exporting file. Please try again later.';
        const body = (error as {error?: Blob | string})?.error;
        if (body instanceof Blob) {
            try {
                const text = (await readExportErrorText(body)).trim();
                this.messageService.processError(text || fallback);
            } catch {
                this.messageService.processError(fallback);
            }
            return;
        }
        this.messageService.processError(typeof body === 'string' && body.trim() ? body : fallback);
    }

    private ganttExportSvg(): SVGElement {
        const svg = this.imgExportElementRef?.nativeElement?.querySelector?.('svg');
        if (!svg) {
            throw new Error('Target element (SVG parent) not found.');
        }
        return svg;
    }

    private async captureGanttTiles(svg: SVGElement): Promise<{columns: number; tiles: Blob[]}> {
        this.exportGanttChart?.applyExportLabelLayout();
        const snapshot = svg.cloneNode(true) as SVGElement;
        const width = Number(snapshot.getAttribute('width'));
        const height = Number(snapshot.getAttribute('height'));
        const xml = svgWithExportFont(snapshot, await loadGanttExportFont());
        const url = URL.createObjectURL(new Blob([xml], {type: 'image/svg+xml;charset=utf-8'}));
        try {
            const image = await this.loadExportImage(url);
            const tiles: Blob[] = [];
            for (const origin of ganttTileOrigins(width, height)) {
                const canvas = document.createElement('canvas');
                canvas.width = origin.width;
                canvas.height = origin.height;
                const context = canvas.getContext('2d');
                if (!context) {
                    throw new Error('Canvas is not available for the Gantt export.');
                }
                context.drawImage(image, origin.x, origin.y, origin.width, origin.height, 0, 0, origin.width, origin.height);
                tiles.push(await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Gantt tile was empty.')), 'image/png');
                }));
            }
            return {columns: ganttTileColumnCount(width), tiles};
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    private loadExportImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Gantt image could not be drawn.'));
            image.src = url;
        });
    }

    private applySelectedVersionOverride(): string | null {
        const versionOverride = this.selectedVersion ?? null;
        if (versionOverride) {
            this.selectedMomentumProgram.gvdpVersion = versionOverride;
            // One-shot. This calculation still uses versionOverride; leaving selectedVersion
            // set would force every later program onto the same template.
            this.selectedVersion = null;
        }
        return versionOverride;
    }

    private applyEcsVersionWhenNoOverride(versionOverride: string | null): void {
        if (this.type !== 'ECS' || versionOverride) {
            return;
        }
        const soleMajor = this.findSoleActiveEcsTemplateMajor();
        if (soleMajor) {
            this.selectedMomentumProgram.gvdpVersion = soleMajor;
        } else if (this.selectedMomentumProgram.gpdpVersion) {
            this.selectedMomentumProgram.gvdpVersion = this.selectedMomentumProgram.gpdpVersion;
        } else {
            this.selectedMomentumProgram.gvdpVersion = null;
        }
    }

    private resolveUnsupportedTemplateVersion(
        versionMap: Map<string, KtsTimingTemplateRow[]> | undefined,
        versionOverride: string | null
    ): boolean {
        if (versionOverride || this.templateMajorExists(versionMap, this.selectedMomentumProgram.gvdpVersion)) {
            return false;
        }
        if (!this.selectedMomentumProgram.gvdpVersion) {
            const versionLabel = this.type === 'ECS' ? 'GPDP Version' : 'GVDP Version';
            this.messageService.processError(
                `The selected program does not have a ${versionLabel} and therefore
                    does not match any known version of the timing template!`);
            this.removeSelectedProgram();
            return true;
        }
        const pieces = this.selectedMomentumProgram.gvdpVersion.split('.');
        if (pieces[0] === '5' && pieces[1] && parseInt(pieces[1], 10) <= KtsComponent.MAX_LEGACY_V5_MINOR) {
            this.selectedMomentumProgram.gvdpVersion = '5.7';
            return false;
        }
        this.messageService.processError(
            `The selected program with version ${this.selectedMomentumProgram.gvdpVersion}
                    does not match any known version of the timing template!`);
        this.removeSelectedProgram();
        return true;
    }

    private templateMajorExists(
        versionMap: Map<string, KtsTimingTemplateRow[]> | undefined,
        version: string
    ): boolean {
        const programMajor = KtsTimingTemplateRow.getMajorVersionFromFullVersion(version);
        return Array.from(versionMap?.keys() ?? []).some(key =>
            KtsTimingTemplateRow.getMajorVersionFromFullVersion(key) === programMajor);
    }

    private applyFastEvPptbVersion(versionMap: Map<string, KtsTimingTemplateRow[]> | undefined): void {
        if (this.type === 'ECS' || this.selectedMomentumProgram.gvdpVersion !== '5.8') {
            return;
        }
        const targetVersion = this.resolveFastEvPptbTargetVersion();
        if (targetVersion === '5.8') {
            return;
        }
        this.applySpecial58TemplateVersion(versionMap, targetVersion);
    }

    private resolveFastEvPptbTargetVersion(): string {
        const isFastProgram = this.selectedMomentumProgram.vehicleProjectClassification?.toUpperCase().includes('FAST EV');
        const hasPPTB = !!(this.selectedMomentumProgram['date12774'] || this.selectedMomentumProgram['date12757']);
        if (isFastProgram) {
            return hasPPTB ? 'FastPPTB5.8' : 'Fast5.8';
        }
        return hasPPTB ? 'PPTB5.8' : '5.8';
    }

    private applySpecial58TemplateVersion(
        versionMap: Map<string, KtsTimingTemplateRow[]> | undefined,
        targetVersion: string
    ): void {
        if (versionMap?.has(targetVersion)) {
            this.selectedMomentumProgram.gvdpVersion = targetVersion;
            return;
        }
        for (const key of versionMap?.keys() ?? []) {
            if (KtsTimingTemplateRow.getMajorVersionFromFullVersion(key) === targetVersion &&
                this.getStatusOfVersion(key, this.type) === 'ACTIVE') {
                this.selectedMomentumProgram.gvdpVersion = key;
                return;
            }
        }
    }

    private rejectMissingTemplateRows(versionMap: Map<string, KtsTimingTemplateRow[]> | undefined): boolean {
        const rowsForVersion = versionMap?.get(this.version);
        if (rowsForVersion?.length) {
            return false;
        }
        this.messageService.processError(
            `No timing template rows are loaded for version ${this.version}. Select a different template version or contact IT for support.`);
        this.removeSelectedProgram();
        return true;
    }

    private rejectDuplicateProgram(
        versionMap: Map<string, KtsTimingTemplateRow[]> | undefined,
        programKey: string
    ): boolean {
        if (!versionMap?.get(this.version)?.[0]?.programData.get(programKey)) {
            return false;
        }
        this.messageService.processError(
            `This program has already been added to template version ${KtsTimingTemplateRow.getMajorVersionFromFullVersion(this.version)}. Select a different program or a different template version.`);
        this.removeSelectedProgram();
        return true;
    }

    private appendProgramColumn(programKey: string): void {
        const typeCols = this.fullCols.get(this.type);
        if (!typeCols.get(this.version)?.length) {
            typeCols.set(this.version, structuredClone(this.cols));
        }
        this.programClassificationMap.set(
            programKey,
            this.selectedMomentumProgram.vehicleProjectClassification
        );
        typeCols.get(this.version).push(
            this.getProgramDateColumn(
                programKey,
                this.selectedMomentumProgram.vehicleProjectClassification
            )
        );
    }

    private applyActiveFullVersionWhenUnlocked(versionOverride: string | null): void {
        if (versionOverride) {
            return;
        }
        for (const key of this.fullCols.get(this.type)?.keys() ?? []) {
            if (KtsTimingTemplateRow.getMajorVersionFromFullVersion(key) === this.selectedMomentumProgram.gvdpVersion &&
                this.getStatusOfVersion(key, this.type) === 'ACTIVE') {
                this.selectedMomentumProgram.gvdpVersion = key;
                break;
            }
        }
    }

    private rejectAvdPre58Version(): boolean {
        if (KtsTimingTemplateRow.getMajorVersionFromFullVersion(this.version) !== '5.7' || this.type !== 'AVD') {
            return false;
        }
        this.messageService.processError('GVDP Version predates 5.8 implementation. ' +
            'We cannot create a timing for this program, please contact David Pelkowski for more information.');
        this.removeSelectedProgram();
        return true;
    }

    private resetVersionOverrideAndStudyMode(): void {
        this.selectedVersion = null;
        this.studyMode = false;
    }

    clearPrograms() {
        this.setLoadingMomentumData(false);
        this.resetVersionOverrideAndStudyMode();
        this.icdePreviousTargetByProgramStage.clear();
        this.icdePreviousAdjustedWeeksByProgramStage.clear();
        this.autoAdjustedPrograms.clear();
        this.selectedMomentumProgram = null;
        this.selectedMomentumProgramList.length = 0;
        this.syncSelectedMomentumProgramState();
        const typeTemplateMap = this.ktsTimingTemplateRowMap.get(this.type);
        if (!typeTemplateMap) {
            return;
        }
        // clear the programData map for each template row
        for (const value of typeTemplateMap.values()) {
            value.map(row => {
                row.programData.clear();
                row.within30Days = false;
                row.within60Days = false;
                row.within90Days = false;
            });
        }
        // reset each entry of fullCols.get(this.type) for the grids to remove program data
        this.fullCols.get(this.type).forEach((value, key) => {
            value.length = 0;
            value.push(...structuredClone(this.cols));
            this.gridApiMap.get(key)?.setGridOption('columnDefs', this.fullCols.get(this.type).get(key));
            this.gridApiMap.get(key)?.setGridOption('rowData', this.ktsTimingTemplateRowMap.get(this.type).get(key));
        });
        this.filterByDuration(null);
        this.refreshGrid();
        this.handleTabClick(this.version);
        if (this.ganttChart) {
            this.makeGanttPrograms();
        }
    }
    refreshGrid() {
        afterNextRender(() => {
            this.gridApiMap.get(this.version)?.setGridOption('columnDefs', this.fullCols.get(this.type).get(this.version));
            this.gridApiMap.get(this.version)?.setGridOption('rowData', this.ktsTimingTemplateRowMap.get(this.type).get(this.version));
        }, { injector: this.injector });
    }

    handleTabClick(key?: string) {
        // sort fullCols.get(this.type) and ktsTimingTemplateRowMap by key to ensure index matches tab
        if (!this.fullCols.get(this.type) || !this.ktsTimingTemplateRowMap.get(this.type)) {
            return;
        }
        this.fullCols.set(this.type, new Map([...this.fullCols.get(this.type)?.entries()].sort(([a], [b]) => a.localeCompare(b))));
        this.ktsTimingTemplateRowMap.set(this.type, new Map([...this.ktsTimingTemplateRowMap.get(this.type).entries()].sort(([a], [b]) => a.localeCompare(b))));
        this.syncKtsTimingTemplateRowMapState();
        if (!key) {
            const activeIndex = this.activeTabIndexSignal();
            if (this.ktsTimingTemplateRowMap.get(this.type).size > activeIndex && activeIndex !== -1) {
                key = [...this.ktsTimingTemplateRowMap.get(this.type).keys()][activeIndex];
            } else {
                return;
            }
        }
        // Apply version and tab index synchronously so template bindings stay stable within a CD cycle (NG0100).
        this.version = key;
        this.setActiveTabIndex(Math.max(
            0,
            Array.from(this.fullCols.get(this.type).keys())
                .sort((a, b) => a.localeCompare(b))
                .indexOf(key)
        ));
        afterNextRender(() => {
            const api = this.gridApiMap.get(key);
            if (api && !api.isDestroyed()) {
                api.sizeColumnsToFit();
            }
        }, { injector: this.injector });
    }

    setupGantt() {
        this.ganttChart = new GanttChart('Task Scheduler', this.makeGanttPrograms());
        if (this.type === 'AVD') {
            this.ganttChart.showSectionBackgrounds = false;
            this.ganttChart.showGanttItems = true;
            this.ganttChart.showSpecialDates = true;
            this.ganttChart.showSpecialDateLines = false;
            this.ganttChart.showSpecialDateValue = true;
        }
    }

    private makeGanttPrograms(): GanttSection[] {
        if (this.type === 'AVD') {
            return this.makeAvdMilestoneGanttPrograms();
        }
        // Loop through each entry in our template row map, and take the 5 key rows.
        // This defines the deliverables we are looking for, and maps them to the display name for the gantt chart section.
        const ganttItemsBySectionByProgId = new Map<string, Map<string, GanttItem[]>>();
        const displayLabelByProgId = new Map<string, string>();
        let id = 0;
        const specialDatesByDeliverableByProgName = new Map<string, Map<string, Date>>();
        const deliverableDateNameRegexs: RegExp[] = [/^ICDe Stage \d$/, /^WHA +\d$/];
        this.ktsTimingTemplateRowMap.get(this.type).forEach((values, key) => {
            values.forEach((ktsTimingTemplateRow) => {
                // check if this is a tracked deliverable date by matching the row.deliverable to the regex:
                if (deliverableDateNameRegexs.some(regex => regex.test(ktsTimingTemplateRow.deliverable))) {
                    ktsTimingTemplateRow.programData.forEach((programTiming: ProgramTiming, programName: string) => {
                        let specialDates: Map<string, Date> = specialDatesByDeliverableByProgName.get(programName);
                        if (!specialDates) {
                            specialDatesByDeliverableByProgName.set(programName, specialDates = new Map<string, Date>());
                        }
                        specialDates.set(ktsTimingTemplateRow.deliverable, programTiming.date);
                    });
                }
                // Check if this is a row that should appear on the Gantt chart:
                if (ktsTimingTemplateRow.batchBuildEvent && ktsTimingTemplateRow.duration > 0 &&
                    ktsTimingTemplateRow.chartLabel && ktsTimingTemplateRow.chartLabel.length > 0) {
                    // This row needs an entry in the gantt chart:
                    for (const [programName, programTiming] of ktsTimingTemplateRow.programData.entries()) {
                        if (!programTiming.date) {
                            continue;
                        }
                        const programId = ktsTimingTemplateRow.version + ': ' + programName;
                        const displayLabel = `${ktsTimingTemplateRow.version}: ${
                            this.formatProgramDisplayName(programName, this.programClassificationMap.get(programName))
                        }`;
                        displayLabelByProgId.set(programId, displayLabel);
                        let ganttItemsBySection: Map<string, GanttItem[]> = ganttItemsBySectionByProgId.get(programId);
                        let ganttItemProgram: GanttItem;
                        if (!ganttItemsBySection) {
                            ganttItemProgram = this.createProgramHeader(displayLabel, programTiming.date, ktsTimingTemplateRow.duration + 'w', () => id++);
                            ganttItemsBySectionByProgId.set(programId, ganttItemsBySection = new Map<string, GanttItem[]>());
                            ganttItemsBySection.set(programName, [ganttItemProgram]);
                        } else {
                            ganttItemProgram = ganttItemsBySection.get(programName)[0];
                        }
                        const ganttItem = new GanttItem({
                            label: ktsTimingTemplateRow.chartLabel,
                            taskId: 'id' + id++,
                            startDate: programTiming.date,
                            duration: ktsTimingTemplateRow.duration + 'w',
                        });
                        const color: string = this.getBlockColor(ktsTimingTemplateRow.batchBuildEvent);
                        ganttItem.setFillColor(color);

                        let ganttItems: GanttItem[] = ganttItemsBySection.get(ktsTimingTemplateRow.batchBuildEvent);
                        if (!ganttItems) {
                            ganttItemsBySection.set(ktsTimingTemplateRow.batchBuildEvent, ganttItems = []);

                            const backgroundGanttItem = new GanttItem({
                                label: '',
                                taskId: 'id' + id++,
                                startDate: programTiming.date,
                                endDate: ganttItem.getDateEnd(),
                                vSpan: 1,
                                rowCount: 0, // Since this is a background coloring, it doesn't occupy any real space
                                type: 'sectionBackground'
                            });
                            backgroundGanttItem.setOpacity(.2);
                            backgroundGanttItem.setFillColor(color);
                            ganttItems.push(backgroundGanttItem);

                            const headerGanttItem = new GanttItem({
                                label: ktsTimingTemplateRow.batchBuildEvent,
                                taskId: 'id' + id++,
                                startDate: programTiming.date,
                                endDate: ganttItem.getDateEnd(),
                                vSpan: 1,
                                rowCount: 1,
                                type: 'sectionHeader'
                            });
                            headerGanttItem.setFillColor('white');
                            headerGanttItem.setTextColor('black');
                            ganttItems.push(headerGanttItem);
                        } else {
                            const backgroundGanttItem = ganttItems[0];
                            const headerGanttItem = ganttItems[1];
                            // We need to ensure that the cover GanttItem's start is the earliest start,
                            // and its end is the latest end of all items in the section.
                            backgroundGanttItem.includeDates(ganttItem.getDateStart(), ganttItem.getDateEnd());
                            headerGanttItem.includeDates(ganttItem.getDateStart(), ganttItem.getDateEnd());

                            // Now make sure the header bar covers the entire section:
                            ganttItemProgram.includeDates(headerGanttItem.getDateStart(), headerGanttItem.getDateEnd());
                        }
                        ganttItems.push(ganttItem);
                        // Set the vSpan of the background item to the item  in the section, not counting itself
                        ganttItems[0].vSpan = ganttItems.length - 1;
                    }
                }
            });
        });

        const ganttSections = new Array<GanttSection>();
        // Now we have a map of program names to a map of milestone names to dates.
        // We can use this to build our Gantt chart
        ganttItemsBySectionByProgId.forEach((ganttItemsBySection, progId) => {
            // Use the order defined in the keyDeliverableToDisplayName array to build out each program's sections.
            const ganttSection = new GanttSection(displayLabelByProgId.get(progId) || progId);
            specialDatesByDeliverableByProgName.forEach((specialDates: Map<string, Date>, progName: string) => {
                if (progId.endsWith(progName)) {
                    specialDates.forEach((date: Date, deliverable: string) => {
                        if (date) {
                            ganttSection.addDate(date, deliverable);
                        }
                    });
                }
            });

            ganttSections.push(ganttSection);
            ganttItemsBySection.forEach((ganttItems) => {
                this.sortSectionItems(ganttItems);
            });
            // Now that each section has its GanttItems sorted, we can sort the GanttSection by the start
            // date of the first GanttItem in each section.
            const orderedSections = this.getOrderedSections(ganttItemsBySection);

            // Now that we have each of the sections sorted, we can add them to the GanttSections in that order:
            orderedSections.forEach(section => {
                const ganttItems = ganttItemsBySection.get(section);
                ganttItems.forEach(ganttItem => {
                    ganttSection.addGanttItem(ganttItem);
                    if (ganttItem.type === 'sectionHeader') {
                        ganttItem.label += `  (end: ${this.formatDate(ganttItem.endDate)})`;
                    }
                });
            });

            // Now make sure the first GanttItem (the header bar) include all the special dates:
            for (const specialDate of ganttSection.specialDates) {
                ganttSection.ganttItems[0]?.includeDates(specialDate.date, specialDate.date);
            }
        });

        // And then make sure each program is sorted by its earliest date:
        this.sortSectionsByEarliestStart(ganttSections);
        return ganttSections;
    }

    private makeAvdMilestoneGanttPrograms(): GanttSection[] {
        const ganttSections = new Array<GanttSection>();
        const ganttItemsBySectionByProgId = new Map<string, Map<string, GanttItem[]>>();
        const displayLabelByProgId = new Map<string, string>();
        const specialDatesByDeliverableByProgName = new Map<string, Map<string, Date[]>>();
        const timingMap = this.ktsTimingTemplateRowMap.get(this.type);
        let id = 0;
        if (!timingMap) {
            return ganttSections;
        }

        timingMap.forEach((values) => {
            values.forEach((ktsTimingTemplateRow) => {
                if (!ktsTimingTemplateRow.chartLabel) {
                    return;
                }
                ktsTimingTemplateRow.programData.forEach((programTiming: ProgramTiming, programName: string) => {
                    if (!programTiming?.date) {
                        return;
                    }
                    let specialDates: Map<string, Date[]> = specialDatesByDeliverableByProgName.get(programName);
                    if (!specialDates) {
                        specialDatesByDeliverableByProgName.set(programName, specialDates = new Map<string, Date[]>());
                    }
                    const datesForLabel = specialDates.get(ktsTimingTemplateRow.chartLabel) || [];
                    if (!datesForLabel.some(existing => existing.getTime() === programTiming.date.getTime())) {
                        datesForLabel.push(programTiming.date);
                        specialDates.set(ktsTimingTemplateRow.chartLabel, datesForLabel);
                    }

                    const programId = `${ktsTimingTemplateRow.version}: ${programName}`;
                    const displayLabel = `${ktsTimingTemplateRow.version}: ${
                        this.formatProgramDisplayName(programName, this.programClassificationMap.get(programName))
                    }`;
                    displayLabelByProgId.set(programId, displayLabel);
                    let ganttItemsBySection: Map<string, GanttItem[]> = ganttItemsBySectionByProgId.get(programId);
                    let ganttItemProgram: GanttItem;
                    const bufferedStartDate = new Date(programTiming.date.getTime() - 7 * 24 * 60 * 60 * 1000);
                    const bufferedEndDate = new Date(programTiming.date.getTime() + 7 * 24 * 60 * 60 * 1000);
                    if (!ganttItemsBySection) {
                        ganttItemProgram = this.createProgramHeader(displayLabel, programTiming.date, '1w', () => id++);
                        ganttItemProgram.includeDates(bufferedStartDate, bufferedEndDate);
                        ganttItemsBySectionByProgId.set(programId, ganttItemsBySection = new Map<string, GanttItem[]>());
                        ganttItemsBySection.set(programName, [ganttItemProgram]);
                    } else {
                        ganttItemProgram = ganttItemsBySection.get(programName)[0];
                        ganttItemProgram.includeDates(bufferedStartDate, bufferedEndDate);
                    }

                    let ganttItems: GanttItem[] = ganttItemsBySection.get(ktsTimingTemplateRow.chartLabel);
                    if (!ganttItems) {
                        ganttItemsBySection.set(ktsTimingTemplateRow.chartLabel, ganttItems = []);

                        const backgroundGanttItem = new GanttItem({
                            label: '',
                            taskId: 'id' + id++,
                            startDate: programTiming.date,
                            endDate: programTiming.date,
                            vSpan: 1,
                            rowCount: 0,
                            type: 'sectionBackground'
                        });
                        backgroundGanttItem.setOpacity(.2);
                        backgroundGanttItem.setFillColor('transparent');
                        ganttItems.push(backgroundGanttItem);

                        const headerGanttItem = new GanttItem({
                            label: ktsTimingTemplateRow.chartLabel,
                            taskId: 'id' + id++,
                            startDate: programTiming.date,
                            endDate: programTiming.date,
                            vSpan: 1,
                            rowCount: 0, // AVD placeholder row is transparent; reserve no vertical layout space.
                            type: 'sectionHeader'
                        });
                        headerGanttItem.setFillColor('transparent');
                        headerGanttItem.setTextColor('transparent');
                        ganttItems.push(headerGanttItem);
                    } else {
                        const backgroundGanttItem = ganttItems[0];
                        const headerGanttItem = ganttItems[1];
                        backgroundGanttItem.includeDates(programTiming.date, programTiming.date);
                        headerGanttItem.includeDates(programTiming.date, programTiming.date);
                        ganttItemProgram.includeDates(headerGanttItem.getDateStart(), headerGanttItem.getDateEnd());
                    }

                    const ganttItem = new GanttItem({
                        label: ktsTimingTemplateRow.chartLabel,
                        taskId: 'id' + id++,
                        startDate: programTiming.date,
                        endDate: programTiming.date,
                        rowCount: 0, // Transparent AVD placeholder: keeps milestone markers without adding blank rows.
                    });
                    ganttItem.setFillColor('transparent');
                    ganttItem.setTextColor('transparent');
                    ganttItems.push(ganttItem);
                    ganttItems[0].vSpan = ganttItems.length - 1;
                });
            });
        });

        ganttItemsBySectionByProgId.forEach((ganttItemsBySection, progId) => {
            const ganttSection = new GanttSection(displayLabelByProgId.get(progId) || progId);
            specialDatesByDeliverableByProgName.forEach((specialDates: Map<string, Date[]>, progName: string) => {
                if (progId.endsWith(progName)) {
                    specialDates.forEach((dates: Date[], deliverable: string) => {
                        dates.forEach((date: Date) => {
                            if (date) {
                                ganttSection.addDate(date, deliverable);
                            }
                        });
                    });
                }
            });

            ganttSections.push(ganttSection);
            ganttItemsBySection.forEach((ganttItems) => {
                this.sortSectionItems(ganttItems);
            });
            const orderedSections = this.getOrderedSections(ganttItemsBySection);

            orderedSections.forEach(section => {
                const ganttItems = ganttItemsBySection.get(section);
                ganttItems.forEach(ganttItem => {
                    ganttSection.addGanttItem(ganttItem);
                });
            });

            for (const specialDate of ganttSection.specialDates) {
                ganttSection.ganttItems[0]?.includeDates(specialDate.date, specialDate.date);
            }
        });

        this.sortSectionsByEarliestStart(ganttSections);
        return ganttSections;
    }

    private getOrderedSections(ganttItemsBySection: Map<string, GanttItem[]>): string[] {
        const orderedSections: string[] = Array.from(ganttItemsBySection.keys());
        orderedSections.sort((a, b) => {
            const startA = ganttItemsBySection.get(a)[0];
            const startB = ganttItemsBySection.get(b)[0];
            if (!startA || !startB) {
                return 0;
            }
            // The header bar should always be first, so if one of the sections is a header, it should be first.
            // We identify the header bar by its rowCount of '2'.
            if (startA.rowCount !== startB.rowCount) {
                if (startA.rowCount === 2) {
                    return -1;
                }
                if (startB.rowCount === 2) {
                    return 1;
                }
            }
            // First compare the start dates, but if they are the same, use the end dates:
            return (startA.getDateStart().getTime() - startB.getDateStart().getTime()) ||
                   (startA.getDateEnd()?.getTime() - startB.getDateEnd()?.getTime()) || 0;
        });
        return orderedSections;
    }

    private sortSectionItems(ganttItems: GanttItem[]): void {
        // Sort the ganttItems, except the background and header items, which are the first 2 elements:
        const firstTwo = ganttItems.splice(0, 2);
        ganttItems.sort((a, b) =>
            (a.getDateStart().getTime() - b.getDateStart().getTime())
            || (a.getDateEnd()?.getTime() - b.getDateEnd()?.getTime()) || 0);
        ganttItems.unshift(...firstTwo);
    }

    private createProgramHeader(
        displayLabel: string,
        startDate: Date,
        duration: string,
        nextId: () => number
    ): GanttItem {
        const ganttItemProgram = new GanttItem({
            label: displayLabel,
            taskId: 'id' + nextId(),
            startDate,
            duration,
            rowCount: 2,
            vSpan: 2,
            type: 'programHeader'
        });
        ganttItemProgram.setTextColor('white');
        ganttItemProgram.setFillColor('black');
        return ganttItemProgram;
    }

    private sortSectionsByEarliestStart(ganttSections: GanttSection[]): void {
        ganttSections.sort((ganttSectionA, ganttSectionB) => {
            // all the items in each section are sorted by start date, so we can just compare the first item in each section
            const startA = ganttSectionA.ganttItems.length && (ganttSectionA.ganttItems.length > 0) && ganttSectionA.ganttItems[0];
            const startB = ganttSectionB.ganttItems.length && (ganttSectionB.ganttItems.length > 0) && ganttSectionB.ganttItems[0];
            return startA && startB ? startA.getDateStart().getTime() - startB.getDateStart().getTime() : 0;
        });
    }
    formatDate(date: Date) {
        const month = date.getMonth() + 1; // Months are zero-indexed
        const day = date.getDate();
        const yearStr = date.getFullYear().toString().slice(-2); // Get the last two digits of the year

        // Pad month and day with leading zeros if necessary
        const monthStr = (month < 10) ? ('0' + month) : ('' + month);
        const dayStr = (day < 10) ? ('0' + day) : ('' + day);
        return `${monthStr}/${dayStr}/${yearStr}`;
    }

    private getBlockColor(batchBuildEvent: string): string {
        // Checked before the generic eSOR rule so existing eSOR sections stay yellow.
        // Allows a colon or other punctuation between the words, and ignores case.
        if (/\bSubComponent\b\W+\beSOR\b\W+Creation\b/i.test(batchBuildEvent)) {
            return '#FF99FF';
        }
        // The \b is a word boundary
        if (/\beSOR\b/.test(batchBuildEvent)) {
            return 'yellow';
        }
        if (/\bHVF\b|\bMEV\b/.test(batchBuildEvent)) {
            return 'yellowgreen';
        }
        if (/\bIVER\b|\bPTB\b|\bPVVB\b|\bPPTB\b/.test(batchBuildEvent)) {
            return 'purple';
        }
        if (/\baPPV\b/.test(batchBuildEvent)) {
            return 'orchid';
        }
        if (/\bPPV\b/.test(batchBuildEvent)) {
            return 'skyblue';
        }
        // MVBns or MVns:
        if (/\bMVB?ns\b/.test(batchBuildEvent)) {
            return 'tan';
        }
        // MVBs or MVs:
        if (/\bMVB?s\b/.test(batchBuildEvent)) {
            return 'orange';
        }
        if (/\bPost SORP\b/.test(batchBuildEvent)) {
            return 'red';
        }
        return 'skyblue';
    }


    onColumnVisible(event: ColumnVisibleEvent<any>, version: string) {
        // check if column was moved outside the grid and is no longer visible
        if (!event.visible) {
            // remove whichever program was dragged off the grid
            const programName = event.column.getColDef().headerName;
            this.removeProgram(version, programName);
        }
    }

    private removeProgram(version: string, programName: string) {
        // find the program name in the fullCols.get(this.type) headers and remove it
        const index = this.fullCols.get(this.type).get(version).findIndex(col => col.headerName === programName);
        if (index >= 0) {
            this.fullCols.get(this.type).get(version).splice(index, 1);
        }
        // if this column is a program column, also remove program data
        if (this.cols.findIndex(col => col.headerName === programName) < 0) {
            // also remove it from the program data of each row
            this.ktsTimingTemplateRowMap.get(this.type).get(version).forEach(row => {
                row.programData.delete(programName);
            });
            this.programClassificationMap.delete(programName);
            const liveProgramName = this.getLiveProgramName(programName);
            // Program-level ICDe state is only valid while the program is actively in the grid.
            this.clearIcdeStateForProgram(liveProgramName);
            this.autoAdjustedPrograms.delete(liveProgramName);
            this.selectedMomentumProgramList = this.selectedMomentumProgramList.filter(
                program => MomentumProgram.getProgramKey(program, this.type) !== programName);
            this.syncSelectedMomentumProgramState();
        }
        this.doTimingCalculations();
        this.filterByDuration(null);
        this.refreshGrid();
        this.handleTabClick(version);
    }

    protected filterByDuration(daysFromToday: number) {
        if (!daysFromToday) {
            this.setFiltersForDurationWindows(null, true);
            this.filterDuration = null;
        } else {
            this.filterDuration = daysFromToday;
            this.setFiltersForDurationWindows('within90Days', daysFromToday === 90);
            this.setFiltersForDurationWindows('within60Days', daysFromToday === 60);
            this.setFiltersForDurationWindows('within30Days', daysFromToday === 30);
        }
        // Dim cells whose date is outside the selected range but are on a row that also has
        // a cell within range (from another program).  Rows with no in-range dates are hidden
        // by the ag-grid column filter above; the opacity change only affects the partial-match cells.
        this.setDisplayForCssClass('.not-within-90-days', !daysFromToday);
        this.setDisplayForCssClass('.within-90-days', !daysFromToday || daysFromToday === 90);
        this.setDisplayForCssClass('.within-60-days', !daysFromToday || daysFromToday >= 60);
        this.setDisplayForCssClass('.within-30-days', !daysFromToday || daysFromToday >= 30);
    }

    // Sets ag-grid column filters to show only rows within the selected duration window.
    // Pass colName=null with doFilter=true to clear all filters.
    private setFiltersForDurationWindows(colName: string, doFilter: boolean) {
        if (!doFilter) {
            return;
        }
        const model = {};
        if (colName) {
            model[colName] = {
                'type': 'true',
            };
        }
        this.gridApiMap.forEach(
            (api, key) => {
                if (this.isGridApiAlive(api)) {
                    api.setFilterModel(model);
                    api.onFilterChanged();
                }
            });
    }

    // This method sets whether to decrease opacity on cells that we do not want to see based on our filters
    // For cells that are showing because another cell in the row is meant to be shown
    private setDisplayForCssClass(styleName: string, isDisplayed: boolean) {
        for (let i = 0; i < document.styleSheets.length; i++) {
            const sheet = document.styleSheets[i];
            for (let j = 0; j < sheet.cssRules.length; j++) {
                const rule = sheet.cssRules[j] as CSSStyleRule;
                if (rule.selectorText === styleName) {
                    if (isDisplayed) {
                        rule.style.setProperty('opacity', '1');
                    } else {
                        rule.style.setProperty('opacity', '0.2');
                    }
                }
            }
        }
    }

    getStatusOfVersionForLabel(key: string, type: string) {
        const versionStatus = this.getStatusOfVersion(key, type);
        if (versionStatus === 'ACTIVE') {
            return '(Released)';
        } else if (versionStatus === 'DRAFT') {
            return '(Draft)';
        } else if (versionStatus === 'ARCHIVED') {
            return '(Archived)';
        }
        return '';
    }

    getStatusOfVersion(key: string, type: string) {
        if (!this.ktsTimingTemplateRowMap.get(type)) {
            return '';
        }
        const versionRows = this.ktsTimingTemplateRowMap.get(type).get(key);
        if (versionRows && versionRows.length) {
            return versionRows[0].status;
        }
        return '';
    }
    saveProgramTiming(forAdmin = false) {
        this.confirmSaveProgramTiming(forAdmin);
    }

    afterProgramRunsLoaded(_programRuns: Map<string, ProgramRun[]>) {
        if (this.type === 'ECS') {
            const lookupFailed = this.programRunsLookupFailed;
            this.programRunsLookupFailed = false;
            if (lookupFailed) {
                const pending = this.pendingEcsSelectedProgram;
                this.pendingEcsSelectedProgram = null;
                this.setLoadingMomentumData(false);
                if (pending && this.version) {
                    this.removeProgram(this.version, this.getProgramKey(pending));
                }
                this.messageService.processError(
                    'Could not check for existing ECS timing. ECS timing was not added.');
                return;
            }
            this.pendingEcsSelectedProgram = null;
            this.setLoadingMomentumData(false);
        }
    }

    buildSaveConfirmationMessage(forAdmin: boolean): string {
        const programsToSave = this.programNamesToSave().split(', ').filter(p => p);
        const programNamesDisplay = programsToSave
            .map(programKey => this.formatProgramDisplayName(programKey))
            .join(', ');
        const autoAdjustMessage = (!forAdmin && !this.allowDuplicates &&
            programsToSave.some(p => this.autoAdjustedPrograms.has(p)))
            ? '<br><br><b>ICDe Stage dates were auto-adjusted toward the previously saved timing.</b>'
            : '';
        return `Are you sure you want to save Program Timings?
            This will save the following programs:
            <b>${programNamesDisplay}</b> and overwrite any existing timings for these programs.
            ${forAdmin ? ' No timings using <b>Draft</b> Templates will be saved.' : ''}${autoAdjustMessage}`;
    }

    confirmSaveProgramTiming(forAdmin = false) {
        this.confirmationService.confirm({
            // message will confirm and then list program names
            message: this.buildSaveConfirmationMessage(forAdmin),
            header: 'Program Timing Confirmation',
            key: 'saveTimingsConfirmation',
            icon: 'pi pi-exclamation-circle',
            acceptButtonStyleClass: 'p-button-text',
            rejectButtonStyleClass: 'p-button-text',
            acceptIcon: 'pi pi-check mr-2',
            rejectIcon: 'pi pi-times mr-2',

            accept: () => {
                const programRunSet = this.collectProgramRunsToSave(forAdmin);
                this.esciService.saveRows(this, programRunSet, 'Program Timing', () => {
                    this.afterProgramTimingSaved(forAdmin);
                });
            }
        });
    }

    private collectProgramRunsToSave(forAdmin: boolean): ProgramRun[] {
        const programRunSet = [];
        for (const [version, rowList] of this.ktsTimingTemplateRowMap.get(this.type).entries()) {
            if (forAdmin && this.isVersionDraft(version)) {
                continue;
            }
            const ktsTimingTemplateRow = rowList[0];
            if (ktsTimingTemplateRow.programData.size) {
                for (const programKey of ktsTimingTemplateRow.programData.keys()) {
                    if (programKey.startsWith(KtsComponent.savedTimingPrefix)) {
                        continue;
                    }
                    const programRun = new ProgramRun();
                    this.selectedMomentumProgramList.forEach(program => {
                        if (MomentumProgram.getProgramKey(program, this.type) === programKey) {
                            programRun.momentumId = program.momentumId;
                            programRun.vehicleProjectClassification = program.vehicleProjectClassification;
                            programRun.programName = program.projectName;
                            programRun.type = this.type;
                            if (this.type === 'ECS') {
                                programRun.gpsProjectId = program.projectId;
                                programRun.momentumId = program.vehProjectId;
                                programRun.gpsRpo = program.gpsRpo;
                            }
                        }
                    });
                    programRun.archived = false;
                    programRun.templateVersion = ktsTimingTemplateRow.fullVersion;
                    programRun.setProgramTimingRowsWithKtsTemplateRows(rowList, programKey);
                    programRun.programTimingRows.forEach(row => {
                        if (row.date) {
                            const date = new Date(row.date);
                            date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
                            row.date = date;
                        }
                    });
                    programRunSet.push(programRun);
                }
            }
        }
        return programRunSet;
    }

    protected afterProgramTimingSaved(_forAdmin = false): void {
        // Hook for KtsAdmin to refresh the PROD primary list after a timing save.
    }

    // return comma separated list of program names
    programNamesToSave() {
        if (!this.ktsTimingTemplateRowMap.get(this.type)) {
            return '';
        }
        const programNames = [];
        for (const [version, rowList] of this.ktsTimingTemplateRowMap.get(this.type).entries()) {
            // do not add any from programs draft versions of templates
            // this will only happen when coming from Admin page
            if (!this.isVersionDraft(version) && rowList[0]?.programData?.size) {
                // add any program names that do not start with old
                for (const programName of rowList[0].programData.keys()) {
                    if (!programName.startsWith(KtsComponent.savedTimingPrefix)) {
                        programNames.push(programName);
                    }
                }
            }
        }
        return programNames.join(', ');
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        const api = this.gridApiMap?.get(this.version);
        if (api && !api.isDestroyed()) {
            api.sizeColumnsToFit();
        }
    }

    public isVersionDraft(version: string, type?: string) {
        return this.getStatusOfVersion(version, type || this.type) === 'DRAFT';
    }

    public isVersionActive(version: string, type?: string) {
        return this.getStatusOfVersion(version, type || this.type) === 'ACTIVE';
    }

    showSavedProgramTiming(programRun: ProgramRun) {
        // we just added the new timing to the template map, so use that version now
        const templateRows = this.ktsTimingTemplateRowMap.get(this.type).get(this.version);
        programRun.templateVersion = this.version;
        const programName = programRun.programName;
        const liveProgramName = this.getLiveProgramName(programName);
        this.replaceIcdeTargetsFromProgramRun(programRun, liveProgramName);
        this.seedIcdeAdjustedWeeksForProgram(liveProgramName);
        let mismatchedVersions = false;
        for (const row of programRun.programTimingRows) {
            const programTiming = new ProgramTiming();
            programTiming.setDataFromProgramTimingRow(row);
            // we have the template row from the map, but we need to set the old program data now
            // we may have exact matches if we are on the same template version, otherwise key off milestones
            let ktsRow = templateRows.find(r => r.id === row.ktsTimingTemplateRow.id &&
                r.fullVersion === row.ktsTimingTemplateRow.fullVersion);
            if (!ktsRow) {
                // this is old program run we are trying to show in our current KTS, match on milestone numbers
                ktsRow = templateRows.find(r => r.phase === 'Milestone' && row.ktsTimingTemplateRow.phase === 'Milestone' &&
                    r.milestone === row.ktsTimingTemplateRow.milestone);
                mismatchedVersions = true;
            }
            if (!ktsRow && !this.allowDuplicates) {
                const stageDeliverable = this.getCanonicalIcdeStageDeliverable(row.ktsTimingTemplateRow.deliverable);
                if (stageDeliverable) {
                    ktsRow = templateRows.find(r => r.deliverable?.trim() === stageDeliverable);
                    mismatchedVersions = true;
                }
            }
            if (!ktsRow) {
                // could not find matching row which is normal for mismatched versions
                continue;
            }
            ktsRow.programData.set(programName, programTiming);
        }
        if (mismatchedVersions) {
            if (!this.allowDuplicates) {
                this.messageService.processSuccess('Existing Saved Program timing has been added to the template. Template versions did not match, so Milestones and ICDe Stage rows (including Exterior Lighting) were aligned for comparison where possible.');
            } else {
                this.messageService.processSuccess('Existing Saved Program timing has been added to the template, but the template versions did not match so only Milestones will be compared.');
            }
        } else {
            this.messageService.processSuccess('Existing Saved Program timing has been added to the template so all dates can be compared.');
        }
        this.ktsTimingTemplateRowMap.get(this.type).set(this.version, templateRows);
        this.syncKtsTimingTemplateRowMapState();
        this.programClassificationMap.set(programName, programRun.vehicleProjectClassification);
        const programDateColumn = this.getProgramDateColumn(programName, programRun.vehicleProjectClassification, true);
        programDateColumn.cellRendererParams = { disableButtons: true };
        this.fullCols.get(this.type).get(this.version).push(programDateColumn);
        this.doTimingCalculations();
        this.refreshGrid();
        this.handleTabClick(this.version);
        this.setupGantt();
    }

    private getCanonicalIcdeStageDeliverable(deliverable: string): string | null {
        const trimmedDeliverable = deliverable?.trim();
        if (!trimmedDeliverable) {
            return null;
        }
        return this.icdeStageDeliverablesByNormalized.get(this.normalizeIcdeDeliverable(trimmedDeliverable)) || null;
    }

    private normalizeIcdeDeliverable(deliverable: string): string {
        return (deliverable || '')
            .toUpperCase()
            .replace(/\s+/g, ' ')
            .trim();
    }

    private getLiveProgramName(programName: string): string {
        if (!programName) {
            return programName;
        }
        return programName.startsWith(KtsComponent.savedTimingPrefix) ?
            programName.slice(KtsComponent.savedTimingPrefix.length) : programName;
    }

    private getProgramStageKey(programName: string, stageDeliverable: string): string {
        return `${programName}|${stageDeliverable}`;
    }

    private clearIcdeStateForProgram(programName: string): void {
        if (!programName) {
            return;
        }
        const programKeyPrefix = `${programName}|`;
        for (const key of Array.from(this.icdePreviousTargetByProgramStage.keys())) {
            if (key.startsWith(programKeyPrefix)) {
                this.icdePreviousTargetByProgramStage.delete(key);
            }
        }
        for (const key of Array.from(this.icdePreviousAdjustedWeeksByProgramStage.keys())) {
            if (key.startsWith(programKeyPrefix)) {
                this.icdePreviousAdjustedWeeksByProgramStage.delete(key);
            }
        }
    }

    private getSavedIcdeReferenceTargetDate(programName: string, milestone: string): Date | null {
        for (const milestoneToken of (milestone || '').split(/\s*\/\s*/)) {
            const referencedStageDeliverable = this.getCanonicalIcdeStageDeliverable(milestoneToken);
            if (!referencedStageDeliverable) {
                continue;
            }
            const referencedStageTargetDate = this.icdePreviousTargetByProgramStage.get(
                this.getProgramStageKey(programName, referencedStageDeliverable)
            );
            if (referencedStageTargetDate) {
                return referencedStageTargetDate;
            }
        }
        return null;
    }

    private replaceIcdeTargetsFromProgramRun(programRun: ProgramRun, liveProgramName?: string): void {
        if (this.allowDuplicates || !programRun?.programTimingRows?.length) {
            return;
        }
        // Track both standard and exterior-lighting ICDe stage rows.
        this.icdeStageDeliverables = new Set([
            ...(programRun.icdStageDeliverables || []),
            ...(programRun.icdStageLightingDeliverables || [])
        ].map(d => d?.trim()).filter(Boolean));
        this.icdeStageDeliverablesByNormalized = new Map(
            Array.from(this.icdeStageDeliverables).map(d => [this.normalizeIcdeDeliverable(d), d] as [string, string])
        );
        const resolvedProgramName = liveProgramName || this.getLiveProgramName(programRun.programName);
        this.clearIcdeStateForProgram(resolvedProgramName);
        for (const row of programRun.programTimingRows) {
            const stageDeliverable = this.getCanonicalIcdeStageDeliverable(row?.ktsTimingTemplateRow?.deliverable);
            if (!stageDeliverable || !row?.date) {
                continue;
            }
            const date = ProgramTiming.shiftServerDateToLocal(row.date);
            this.icdePreviousTargetByProgramStage.set(
                this.getProgramStageKey(resolvedProgramName, stageDeliverable),
                date
            );
            if (row.adjustedWeeks !== undefined && row.adjustedWeeks !== null) {
                this.icdePreviousAdjustedWeeksByProgramStage.set(
                    this.getProgramStageKey(resolvedProgramName, stageDeliverable),
                    row.adjustedWeeks
                );
            }
        }
    }

    private seedIcdeAdjustedWeeksForProgram(programName: string): void {
        if (this.allowDuplicates || !programName) {
            return;
        }
        let seededCount = 0;
        for (const rows of this.ktsTimingTemplateRowMap.get(this.type)?.values() || []) {
            for (const row of rows) {
                const stageDeliverable = this.getCanonicalIcdeStageDeliverable(row.deliverable);
                if (!stageDeliverable) {
                    continue;
                }
                const timing = row.programData.get(programName);
                if (!timing?.date) {
                    continue;
                }
                const stageKey = this.getProgramStageKey(programName, stageDeliverable);
                const currentAdjustedWeeks = timing.adjustedWeeks;
                const hasIcdeStageMilestoneReference = (row.milestone || '')
                    .split(/\s*\/\s*/)
                    .some(milestone => Boolean(this.getCanonicalIcdeStageDeliverable(milestone)));
                const previousAdjustedWeeks = this.icdePreviousAdjustedWeeksByProgramStage.get(stageKey);
                const previousTargetDate = this.icdePreviousTargetByProgramStage.get(stageKey);

                // ICDe rows that reference another ICDe stage (for example exterior lighting rows)
                // are relative calculations. Use saved stage/lighting target relationship first so
                // changed template offsets across versions still align to old saved dates.
                if (hasIcdeStageMilestoneReference) {
                    const previousReferenceTargetDate = this.getSavedIcdeReferenceTargetDate(programName, row.milestone);
                    if (previousTargetDate && previousReferenceTargetDate) {
                        const weeksFromMilestone = Number(row.weeksFromMilestone) || 0;
                        const seededAdjustedWeeks =
                            CommonHelperService.calculateWeekOffsetBetweenDates(previousTargetDate, previousReferenceTargetDate)
                            - weeksFromMilestone;
                        if (seededAdjustedWeeks !== currentAdjustedWeeks) {
                            timing.adjustedWeeks = seededAdjustedWeeks;
                            seededCount += 1;
                        }
                        continue;
                    }
                    if (previousAdjustedWeeks !== undefined && previousAdjustedWeeks !== currentAdjustedWeeks) {
                        timing.adjustedWeeks = previousAdjustedWeeks;
                        seededCount += 1;
                    }
                    continue;
                }
                if (previousTargetDate) {
                    // Prefer matching the previously saved target date so changes in momentum baseline
                    // still carry forward ICDe intent, even when historical adjustedWeeks was 0/default.
                    const seededAdjustedWeeks = CommonHelperService.calculateWeekOffsetBetweenDates(previousTargetDate, timing.date);
                    if (seededAdjustedWeeks !== currentAdjustedWeeks) {
                        timing.adjustedWeeks = seededAdjustedWeeks;
                        seededCount += 1;
                    }
                    continue;
                }
                if (previousAdjustedWeeks !== undefined && previousAdjustedWeeks !== currentAdjustedWeeks) {
                    timing.adjustedWeeks = previousAdjustedWeeks;
                    seededCount += 1;
                }
            }
        }
        if (seededCount > 0) {
            this.autoAdjustedPrograms.add(programName);
        } else {
            this.autoAdjustedPrograms.delete(programName);
        }
    }

    protected getPrefix() {
        if (this.type === 'PROD') {
            return 'PSDS';
        }
        return this.type;
    }

    // Non- PROD should not have esor option, but visible flag does not work so need to do this
    get visibleDownloadButtonOptions() {
        if (this.type === 'PROD') {
            return this.downloadButtonOptions;
        }
        return this.avdDownloadButtonOptions;
    }

}
